import { createServerFn } from "@tanstack/react-start";

import type { Json } from "./oddpool.server";

const BROWSE_TOPICS = ["bitcoin", "ethereum", "crypto", "fed", "election", "ai"];
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * The upstream has no "list all events" endpoint, so the landing feed fans out
 * over a few topics server-side, merges, dedupes and sorts by volume.
 */
async function browse(limitPerTopic: number): Promise<Json> {
  const { oddpoolFetch, buildQuery } = await import("./oddpool.server");
  const byId = new Map<string, Record<string, Json>>();

  for (const topic of BROWSE_TOPICS) {
    try {
      const res = await oddpoolFetch(`/search/events${buildQuery({ q: topic, limit: limitPerTopic })}`);
      if (!res.ok) {
        if (res.status === 429) await sleep(600);
        continue;
      }
      const rows = await res.json().catch(() => []);
      if (!Array.isArray(rows)) continue;
      for (const r of rows) {
        const id = r?.event_id;
        if (typeof id === "string" && !byId.has(id)) byId.set(id, { ...r, topic });
      }
    } catch {
      /* ignore a single failing topic */
    }
    await sleep(120);
  }

  return Array.from(byId.values()).sort(
    (a, b) => Number(b["total_volume"] ?? 0) - Number(a["total_volume"] ?? 0),
  );
}

export const oddpoolSearch = createServerFn({ method: "POST" })
  .inputValidator(
    (input: {
      kind?: string;
      query?: string;
      series_id?: string;
      event_id?: string;
      settled_after?: string;
      settled_before?: string;
      limit?: number;
    }) => input ?? {},
  )
  .handler(async ({ data }) => {
    const { oddpoolJson, buildQuery } = await import("./oddpool.server");
    const { kind = "events", query, series_id, event_id, settled_after, settled_before, limit = 50 } = data;

    if (kind === "browse") return await browse(Math.min(Number(limit) || 25, 50));

    const qs = buildQuery({ q: query, series_id, settled_after, settled_before, limit });
    let path: string;
    switch (kind) {
      case "markets":
        path = `/search/markets${qs}`;
        break;
      case "series":
        path = `/search/series${qs}`;
        break;
      case "recent_events":
        path = `/search/recent/events${buildQuery({ limit })}`;
        break;
      case "recent_markets":
        path = `/search/recent/markets${buildQuery({ limit })}`;
        break;
      case "event_markets":
        if (!event_id) return { error: "event_id required", status: 400 };
        path = `/search/events/${encodeURIComponent(event_id)}/markets`;
        break;
      default:
        path = `/search/events${qs}`;
    }
    return await oddpoolJson(path);
  });

export const oddpoolArbitrage = createServerFn({ method: "POST" })
  .inputValidator((input: { mode?: string; min_net_cents?: number; limit?: number }) => input ?? {})
  .handler(async ({ data }) => {
    const { oddpoolJson, buildQuery } = await import("./oddpool.server");
    const { mode = "current", min_net_cents = 0.5, limit = 50 } = data;
    const path =
      mode === "difference"
        ? `/arbitrage/current/difference${buildQuery({ limit })}`
        : `/arbitrage/current${buildQuery({ min_net_cents, limit })}`;
    return await oddpoolJson(path);
  });

export const oddpoolWhales = createServerFn({ method: "POST" })
  .inputValidator((input: { kind?: string; event_id?: string; market_id?: string; limit?: number }) => input ?? {})
  .handler(async ({ data }) => {
    const { oddpoolJson, buildQuery } = await import("./oddpool.server");
    const { kind = "feed", event_id, market_id, limit = 100 } = data;

    let path: string;
    switch (kind) {
      case "events":
        path = `/whales/user/events`;
        break;
      case "stats":
        path = `/whales/user/stats`;
        break;
      case "event_trades":
      case "event_stats":
        if (!event_id) return { error: "event_id required", status: 400 };
        path =
          kind === "event_stats"
            ? `/whales/user/event/${encodeURIComponent(event_id)}/stats`
            : `/whales/user/event/${encodeURIComponent(event_id)}${buildQuery({ limit })}`;
        break;
      case "markets":
        path = `/whales/user/markets`;
        break;
      case "market_stats":
        if (!market_id) return { error: "market_id required", status: 400 };
        path = `/whales/user/market/${encodeURIComponent(market_id)}/stats`;
        break;
      case "market_trades":
        if (!market_id) return { error: "market_id required", status: 400 };
        path = `/whales/user/market/${encodeURIComponent(market_id)}/trades${buildQuery({ limit })}`;
        break;
      default:
        path = `/whales/user/feed${buildQuery({ limit })}`;
    }
    return await oddpoolJson(path);
  });

export const oddpoolOhlcv = createServerFn({ method: "POST" })
  .inputValidator(
    (input: {
      kind?: string;
      event_id?: string;
      market_ids?: string;
      interval?: string;
      start?: string;
      end?: string;
    }) => input ?? {},
  )
  .handler(async ({ data }) => {
    const { oddpoolJson, buildQuery } = await import("./oddpool.server");
    const { kind = "event", event_id, market_ids, interval = "1d", start, end } = data;

    let path: string;
    if (kind === "event") {
      if (!event_id) return { error: "event_id required", status: 400 };
      path = `/events/${encodeURIComponent(event_id)}/ohlcv${buildQuery({ interval, start, end })}`;
    } else {
      if (!market_ids) return { error: "market_ids required", status: 400 };
      path = `/markets/ohlcv${buildQuery({ market_ids, interval, start, end })}`;
    }
    return await oddpoolJson(path);
  });

type HistoryInput = {
  kind?: string;
  market_id?: string;
  token_id?: string;
  start?: string;
  end?: string;
  interval?: string;
  limit?: number;
};

export const oddpoolHistoricalKalshi = createServerFn({ method: "POST" })
  .inputValidator((input: HistoryInput) => input ?? {})
  .handler(async ({ data }) => {
    const { oddpoolJson, buildQuery } = await import("./oddpool.server");
    const { kind = "top_of_book", market_id, start, end, interval, limit = 200 } = data;
    if (!market_id) return { error: "market_id required", status: 400 };

    const qs = buildQuery({ market_id, start, end, interval, limit: Math.min(Number(limit) || 200, 200) });
    const path =
      kind === "orderbook"
        ? `/historical/kalshi/orderbook${qs}`
        : kind === "trades"
          ? `/historical/kalshi/trades${qs}`
          : `/historical/kalshi/top-of-book${qs}`;
    return await oddpoolJson(path);
  });

export const oddpoolHistoricalPolymarket = createServerFn({ method: "POST" })
  .inputValidator((input: HistoryInput) => input ?? {})
  .handler(async ({ data }) => {
    const { oddpoolJson, buildQuery } = await import("./oddpool.server");
    const { kind = "top_of_book", market_id, token_id, start, end, interval, limit = 200 } = data;
    if (!market_id && !token_id) return { error: "market_id or token_id required", status: 400 };

    const qs = buildQuery({
      market_id,
      token_id,
      start,
      end,
      interval,
      limit: Math.min(Number(limit) || 200, 200),
    });
    const path =
      kind === "orderbook"
        ? `/historical/polymarket/orderbook${qs}`
        : kind === "trades"
          ? `/historical/polymarket/trades${qs}`
          : `/historical/polymarket/top-of-book${qs}`;
    return await oddpoolJson(path);
  });

export const oddpoolFeedCatalog = createServerFn({ method: "POST" })
  .inputValidator((input: { kind?: string; event_type?: string }) => input ?? {})
  .handler(async ({ data }) => {
    const { oddpoolJson, buildQuery } = await import("./oddpool.server");
    const { kind = "macro", event_type } = data;
    const path =
      kind === "crypto"
        ? `/feeds/catalog/crypto${buildQuery({ event_type })}`
        : `/feeds/catalog/macro${buildQuery({ event_type })}`;
    return await oddpoolJson(path);
  });
